// ── Per-tier Settings UX rules (issue #779) ────────────────────────────────
// Single source of truth for which Settings sections are visible per tier
// and what label to use for the Account/Settings nav-item + page heading.
//
// Why a helper rather than inline checks: SettingsPage.tsx (1828 LOC) and
// SettingsOrganisationPage.tsx (838 LOC) had scattered `isSoloTier(...)`
// and `isPartner` conditionals already; this consolidates the rules so
// adding a tier or a section means editing one map, and so a unit test
// can exhaustively assert every (tier × section) outcome.
//
// Reads tier values as plain strings (canonical 5-tier model after CFG.TIER
// cleanup #775: INDIVIDUAL, STARTER, GROWTH, ENTERPRISE, PLATFORM_PARTNER).
// Unknown tiers (incl. retired aliases like BLUEPRINT_CORE/_FULL) fall
// through to the "treat as basic team tier" branch — no crash, mostly-
// reasonable visibility defaults so a stale token can't blank the page.

import { isSoloTier } from './tier';

/** Identifier for a Settings section. Add a value here when a new section
 *  lands so the visibility map stays exhaustive. */
export type SettingsSection =
  | 'appearance'
  | 'github-connection'
  | 'byoi'
  | 'billing'
  | 'organisation-profile'
  | 'team-members'
  | 'estimation-display'
  | 'integrations'
  | 'platform-access-log'
  | 'agent-model-config'
  | 'notification-preferences'
  | 'whatsapp-channel'
  | 'ms-teams-channel'
  | 'slack-channel'
  | 'tool-integrations'
  | 'phase-gate-templates'
  | 'escalation-automation'
  | 'phase-config-defaults';

// Sections that INDIVIDUAL (solo plan) tenants should NOT see. Solo
// tenants have no team and run one project at a time, so anything
// team-collaboration, multi-project-defaults, or org-admin-shaped is
// hidden. The Team Members section was already conditional via an
// upgrade modal — kept that pattern by hiding the section entirely
// here; the upgrade prompt now belongs on the Plan card (#780).
const HIDDEN_FOR_INDIVIDUAL: ReadonlySet<SettingsSection> = new Set([
  'billing',              // partner-gated; INDIVIDUAL is never a partner
  'team-members',         // no team
  'platform-access-log',  // tenant-admin signal — meaningless solo
  'ms-teams-channel',     // no MS Teams workspace without an org
  'tool-integrations',    // Jira/ADO/Confluence/Notion — overkill solo
  'phase-gate-templates', // no team approvals
  'escalation-automation',// no team to escalate to
  'phase-config-defaults',// runs one project at a time; defaults overkill
]);

// `billing` is the one section gated by partner_accounts membership
// rather than tier alone (a STARTER user who is also a partner sees
// Billing; a PLATFORM_PARTNER tenant who lost partner_accounts membership
// would not). The visibility helper takes a separate `isPartner` flag
// for this case so callers don't have to special-case Billing inline.
const PARTNER_ONLY_SECTIONS: ReadonlySet<SettingsSection> = new Set([
  'billing',
]);

/**
 * Decide whether a Settings section is visible for the given tier.
 *
 * Pass `isPartner` for partner_accounts-membership-gated sections (today
 * only `billing`); other sections ignore it.
 */
export function isSectionVisible(
  section: SettingsSection,
  tier: string | null | undefined,
  opts: { isPartner?: boolean } = {},
): boolean {
  // Partner-gated sections are visible iff the user is actually a partner.
  // Tier alone isn't enough — a partner running on STARTER (rare but
  // possible during onboarding) should still see Billing.
  if (PARTNER_ONLY_SECTIONS.has(section)) {
    return opts.isPartner === true;
  }

  // Solo plan: hide team/admin-shaped sections.
  if (isSoloTier(tier)) {
    return !HIDDEN_FOR_INDIVIDUAL.has(section);
  }

  // Team tiers (STARTER, GROWTH, ENTERPRISE, PLATFORM_PARTNER) and any
  // unknown/legacy tier value: show every non-partner-gated section.
  // Unknown values default to "show" so a stale token / retired alias
  // doesn't blank the entire Settings page — better to render extra
  // sections than to render nothing.
  return true;
}

/**
 * Resolve the user-facing label for the Account/Settings nav-item and page
 * heading. Solo tenants get "Account" (single-person concept); team tiers
 * keep "Settings" / "Organisation" as today.
 *
 * `surface` distinguishes the two locations that need this label:
 *   - 'sidebar'   → top-level nav: "Account" or "Settings"
 *   - 'page'      → /settings/organisation page heading: "Account" or
 *                   "Organisation" (different baseline word)
 */
export function getAccountSectionLabel(
  tier: string | null | undefined,
  surface: 'sidebar' | 'page',
): string {
  if (isSoloTier(tier)) return 'Account';
  return surface === 'sidebar' ? 'Settings' : 'Organisation';
}
